import React from 'react';
import { AcquirerType, IRestaurantBankSettings } from 'types';
import {
	RSB_SBP_QR_BANK_SETTINGS_ID,
	YANDEX_PAY_QR_BANK_SETTINGS_ID,
	VIVA_WALLET_SOFT_POS_QR_BANK_SETTINGS_ID,
	BEST_2_PAY_QR_BANK_SETTINGS_ID,
	YANDEX_CHECK_QR_BANK_SETTINGS_ID,
	ADYEN_BANK_SETTINGS_ID,
	TOUCAN_BANK_SETTINGS_ID,
	TBANK_BANK_SETTINGS_ID,
} from 'appConstants';
import FullInfoField from 'components/FullInfoField/FullInfoField';
import { IsFeeFiscalizedCheckbox } from './components/IsFeeFiscalizedCheckbox';
import { DisplayTipsCheckbox } from './components/DisplayTipsCheckbox';
import { GenerateQrInstantlyField } from './components/GenerateQrInstantlyField';
import { SplitPayments3SCheckbox } from './components/SplitPayments3SCheckbox';
import DownloadBtn from './components/DownloadButton';
import style from './BankSettingFields.module.scss';

interface IProps {
	bankSetting: IRestaurantBankSettings;
}

export const BankSettingFields: React.FC<IProps> = ({ bankSetting }) => {
	const isQrAcquirer = bankSetting.acquirer?.type === AcquirerType.QR;

	return (
		<>
			<div className={style.fields}>
				<div className={style.row}>
					<FullInfoField label="priorityLabel" value={bankSetting.priority} />
					<FullInfoField label="merchantIdLabel" value={bankSetting.merchantId} />
				</div>
				<div className={style.row}>
					<FullInfoField label="merchantNameLabel" value={bankSetting.merchantName} />
					<FullInfoField label="typeLabel" value={bankSetting.acquirer?.name} />
				</div>
				<div className={style.row}>
					<div className={style.flex1}>
						<IsFeeFiscalizedCheckbox isFeeFiscalized={bankSetting.isFeeFiscalized} />
					</div>
					<div className={style.flex1}>
						<DisplayTipsCheckbox isTipsSupported={bankSetting.isTipsSupported} />
					</div>
				</div>
				<div className={style.row}>
					<div className={style.flex1}>
						{isQrAcquirer ? (
							<GenerateQrInstantlyField generateQrInstantly={bankSetting.generateQrInstantly} />
						) : (
							<FullInfoField label="paymentTypeIdLabel" value={bankSetting.paymentTypeId} />
						)}
					</div>
					<div className={style.flex1}>
						{isQrAcquirer && <FullInfoField label="paymentTypeIdLabel" value={bankSetting.paymentTypeId} />}
						{bankSetting.acquirer?.id === TOUCAN_BANK_SETTINGS_ID && (
							<FullInfoField label="smsCodeLabel" value={bankSetting.smsCode} />
						)}
					</div>
				</div>
				{bankSetting.acquirer?.id === RSB_SBP_QR_BANK_SETTINGS_ID && (
					<div className={style.row}>
						<FullInfoField label="accountNumberLabel" value={bankSetting.sbpAccount} />
						<FullInfoField label="loginLabel" value={bankSetting.sbpUserLogin} />
					</div>
				)}
				{bankSetting.acquirer?.id === YANDEX_PAY_QR_BANK_SETTINGS_ID && (
					<>
						<FullInfoField label="apiKeyLabel" value={bankSetting.yandexApiKey} />
						<FullInfoField
							label="yandexPaymentTypeLabel"
							value={bankSetting.yandexPaymentTypes?.map((el) => el).join(', ')}
						/>
					</>
				)}
				{bankSetting.acquirer?.id === VIVA_WALLET_SOFT_POS_QR_BANK_SETTINGS_ID && (
					<div className={style.row}>
						<FullInfoField label="APIKeyLabel" value={bankSetting.vivaApiKey} />
						<FullInfoField label="APISecretLabel" value={bankSetting.vivaApiSecret} />
					</div>
				)}
				{bankSetting.acquirer?.id === BEST_2_PAY_QR_BANK_SETTINGS_ID && (
					<>
						<div className={style.row}>
							<FullInfoField label="accNumberLabel" value={bankSetting.accNumber} />
							<FullInfoField label="sectorFieldLabel" value={bankSetting.sector} />
						</div>
						<div className={style.row}>
							<FullInfoField label="sdRefLabel" value={bankSetting.sdRef} />
							<div className={style.flex1} />
						</div>
					</>
				)}
				{bankSetting.acquirer?.id === YANDEX_CHECK_QR_BANK_SETTINGS_ID && (
					<div className={style.row}>
						<FullInfoField label="yandexChequeApiTokenLabel" value={bankSetting.yandexChequeApiToken} />
						<FullInfoField
							label="yandexChequeApiPartnerIdLabel"
							value={bankSetting.yandexChequeApiPartnerId}
						/>
					</div>
				)}
				{bankSetting.acquirer?.id === ADYEN_BANK_SETTINGS_ID && (
					<>
						<div className={style.row}>
							<FullInfoField label="merchantAccountLabel" value={bankSetting?.adyen?.merchantAccount} />
							<FullInfoField label="storeLabel" value={bankSetting?.adyen?.store} />
						</div>
						<div className={style.row}>
							<FullInfoField label="xApiKeyLabel" value={bankSetting?.adyen?.apiKey} />
							<FullInfoField
								label="checkoutUrlPrefixLabel"
								value={bankSetting?.adyen?.checkoutUrlPrefix}
							/>
						</div>
						<div className={style.row}>
							<div className={style.flex1}>
								<SplitPayments3SCheckbox splitPayments3S={!!bankSetting?.adyen?.splitPayments3S} />
							</div>
							<div className={style.flex1} />
						</div>
						{bankSetting?.adyen?.splitPayments3S && (
							<div className={style.row}>
								<FullInfoField
									label="merchantBalanceAccountIdLabel"
									value={bankSetting.adyen.merchantBalanceAccountId}
								/>
								<FullInfoField
									label="kmBalanceAccountIdLabel"
									value={bankSetting.adyen.kmBalanceAccountId}
								/>
							</div>
						)}
					</>
				)}
				{bankSetting.acquirer?.id === TOUCAN_BANK_SETTINGS_ID && (
					<FullInfoField label="masterWalletLabel" value={bankSetting.masterWallet} />
				)}
				{bankSetting.acquirer?.id === TBANK_BANK_SETTINGS_ID && (
					<div className={style.row}>
						<div className={style.flex1}>
							<FullInfoField label="tBankPlaceIdLabel" value={bankSetting.tBankPlaceId} />
						</div>
						<div className={style.flex1} />
					</div>
				)}
			</div>
			<DownloadBtn filePath={bankSetting.paymentTemplateFilePath} />
			<div className={style.divider} />
		</>
	);
};
